import { isBlobNode } from "@ide/shared";
import { toast } from "sonner";
import { create } from "zustand";
import type { ContainerSpec, EngineStatus, LocalRun, LocalRunKind } from "@ide/shared/desktop";
import { ProjectSession, type FileHandle } from "@/collab/session";
import { desktop } from "@/desktop/bridge";
import { errorMessage, get, post } from "@/lib/api";
import type { AppConfig, RoomToken } from "@/lib/types";
import { absoluteWs } from "@/workspace/context";

/**
 * Execuções locais (Kompil Desktop): Live Servers e contêineres de vários projetos ao mesmo tempo.
 * O app guarda as execuções; esta janela mantém os arquivos em dia com uma sessão própria do
 * projeto (silenciosa: não aparece na presença), que continua viva fora do projeto. Recarregou a
 * página ou fechou a janela dona? A execução é retomada aqui (os arquivos vão de novo).
 */
export const runsAvailable = Boolean(desktop?.runs);

export const useLocalRuns = create<{ runs: LocalRun[]; engine: EngineStatus | null }>(() => ({
  runs: [],
  engine: null,
}));

export const isActive = (r: LocalRun) => r.status === "starting" || r.status === "running";

/** Execução ativa do projeto (uma de cada tipo por projeto). */
export function activeRun(runs: LocalRun[], projectId: string, kind: LocalRunKind) {
  return runs.find((r) => r.projectId === projectId && r.kind === kind && isActive(r)) ?? null;
}

const WEB_FILE = /\.(html?|css|m?js|json|map|svg|xml|txt|md|csv|webmanifest)$/i;
/** Gerados ou baixados: não vão para o contêiner (o próprio contêiner os cria). */
const SKIP = /(^|\/)(node_modules|\.git|\.venv|venv|__pycache__|\.next|\.nuxt|dist|build|target|out)(\/|$)/;
const LIMITS: Record<LocalRunKind, { files: number; bytes: number }> = {
  live: { files: 400, bytes: 18_000_000 },
  container: { files: 2000, bytes: 18_000_000 },
};

/** Pasta servida pelo Live Server: onde estiver o index.html mais perto da raiz. */
export function siteRoot(paths: string[]): string {
  if (paths.includes("index.html")) return "";
  const candidates = paths
    .filter((p) => p.endsWith("/index.html"))
    .sort((a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b));
  const preferred = candidates.find((p) => /^(public|dist|site|www|docs)\/index\.html$/.test(p));
  const found = preferred ?? candidates[0];
  return found ? found.slice(0, -"index.html".length) : "";
}

let syncUrl: Promise<string> | null = null;
const wsUrl = () => (syncUrl ??= get<AppConfig>("/config").then((c) => absoluteWs(c.syncUrl)));

/** Mantém os arquivos de uma execução em dia (edições suas e do time). */
class RunSync {
  readonly session: ProjectSession;
  private handles = new Map<string, { handle: FileHandle; path: string; off: () => void }>();
  private pending: Record<string, string | null> = {};
  private timer: ReturnType<typeof setTimeout> | null = null;
  private offTree: (() => void) | null = null;
  private attached = false;
  private warned = false;
  root: string;

  constructor(
    readonly runId: string,
    readonly projectId: string,
    readonly kind: LocalRunKind,
    root: string | null,
    url: string,
  ) {
    this.root = root ?? "";
    this.session = new ProjectSession({
      projectId,
      syncUrl: url,
      presence: false,
      fetchTicket: () => post<RoomToken>(`/projects/${projectId}/room-token`),
    });
  }

  /** Conecta, espera a árvore e devolve os arquivos (caminho relativo à raiz → conteúdo). */
  async open(guessRoot: boolean): Promise<Record<string, string>> {
    await this.session.start();
    await this.session.whenTreeSynced(15_000);
    const paths = [...this.session.tree().paths.values()];
    if (guessRoot) {
      if (!paths.some((path) => path === "index.html" || path.endsWith("/index.html"))) {
        throw new Error("A pré-visualização precisa de um arquivo index.html no projeto.");
      }
      this.root = siteRoot(paths);
    }
    this.offTree = this.session.on("tree", () => void this.collect());
    return this.collect();
  }

  private wanted(): Map<string, string> {
    const tree = this.session.tree();
    const out = new Map<string, string>();
    const limit = LIMITS[this.kind].files;
    for (const [id, path] of tree.paths) {
      // Binários (banco, imagem…) não têm texto: a execução usa a pasta do computador para eles.
      if (
        tree.nodes.get(id)?.type !== "file" ||
        isBlobNode(tree.nodes.get(id)) ||
        !path.startsWith(this.root)
      )
        continue;
      const rel = path.slice(this.root.length);
      if (SKIP.test(rel) || (this.kind === "live" && !WEB_FILE.test(rel))) continue;
      if (out.size >= limit) {
        if (!this.warned) toast(`Projeto grande: só os primeiros ${limit} arquivos vão para a execução.`);
        this.warned = true;
        break;
      }
      out.set(id, rel);
    }
    return out;
  }

  private queue(path: string, content: string | null) {
    if (!this.attached) return;
    this.pending[path] = content;
    this.timer ??= setTimeout(() => this.flush(), 200);
  }

  private flush() {
    this.timer = null;
    const changes = this.pending;
    this.pending = {};
    if (Object.keys(changes).length) desktop?.runs.update(this.runId, changes);
  }

  /** Abre (e passa a observar) os arquivos; devolve o conteúdo atual de cada um. */
  async collect(): Promise<Record<string, string>> {
    const wanted = this.wanted();
    for (const [id, entry] of this.handles) {
      if (wanted.get(id) === entry.path) continue;
      entry.off();
      entry.handle.release();
      this.handles.delete(id);
      this.queue(entry.path, null);
    }
    const files: Record<string, string> = {};
    let bytes = 0;
    await Promise.all(
      [...wanted].map(async ([id, path]) => {
        let entry = this.handles.get(id);
        if (!entry) {
          const handle = this.session.openFile(id);
          const observer = () => this.queue(path, handle.text.toString());
          handle.text.observe(observer);
          entry = { handle, path, off: () => handle.text.unobserve(observer) };
          this.handles.set(id, entry);
          await this.session.loaded(handle);
          this.queue(path, handle.text.toString());
        }
        const text = entry.handle.text.toString();
        bytes += text.length;
        if (bytes <= LIMITS[this.kind].bytes) files[path] = text;
      }),
    );
    return files;
  }

  attach() {
    this.attached = true;
  }

  destroy() {
    if (this.timer) clearTimeout(this.timer);
    this.offTree?.();
    for (const entry of this.handles.values()) {
      entry.off();
      entry.handle.release();
    }
    this.handles.clear();
    this.session.destroy();
  }
}

const syncs = new Map<string, RunSync>();
/** Execuções sendo criadas por esta janela (ainda não chegaram na lista do app). */
const creating = new Set<string>();

async function adopt(run: LocalRun) {
  const sync = new RunSync(run.id, run.projectId, run.kind, run.root, await wsUrl());
  syncs.set(run.id, sync);
  try {
    const files = await sync.open(false);
    await desktop!.runs.resync(run.id, files);
    sync.attach();
  } catch {
    syncs.delete(run.id);
    sync.destroy();
  }
}

function reconcile(runs: LocalRun[]) {
  for (const run of runs) {
    if (run.mine && isActive(run) && !syncs.has(run.id) && !creating.has(run.id)) void adopt(run);
  }
  for (const [id, sync] of syncs) {
    if (creating.has(id)) continue;
    const run = runs.find((r) => r.id === id);
    if (!run || !run.mine || !isActive(run)) {
      sync.destroy();
      syncs.delete(id);
    }
  }
}

let started = false;
/** Liga a lista ao app (uma vez, na raiz). */
export function initLocalRuns() {
  if (started || !desktop?.runs) return;
  started = true;
  desktop.runs.onChange((runs) => {
    useLocalRuns.setState({ runs });
    reconcile(runs);
  });
  desktop.engine.onStatus((engine) => useLocalRuns.setState({ engine }));
  void desktop.runs.list().then((runs) => {
    useLocalRuns.setState({ runs });
    reconcile(runs);
  });
}

export async function refreshEngine() {
  if (!desktop?.engine) return null;
  const engine = await desktop.engine.status();
  useLocalRuns.setState({ engine });
  return engine;
}

export interface RunProject {
  id: string;
  name: string;
}

async function startRun(
  project: RunProject,
  kind: LocalRunKind,
  start: (input: {
    id: string;
    projectId: string;
    projectName: string;
    root: string;
    files: Record<string, string>;
  }) => Promise<{ ok: boolean; run?: LocalRun; error?: string }>,
): Promise<LocalRun | null> {
  if (!desktop?.runs) {
    toast("Atualize o Kompil Desktop para rodar projetos na sua máquina.");
    return null;
  }
  const id = crypto.randomUUID();
  creating.add(id);
  const sync = new RunSync(id, project.id, kind, null, await wsUrl());
  syncs.set(id, sync);
  try {
    const files = await sync.open(kind === "live");
    const res = await start({ id, projectId: project.id, projectName: project.name, root: sync.root, files });
    if (!res.ok || !res.run) throw new Error(res.error ?? "Não foi possível iniciar.");
    sync.attach();
    return res.run;
  } catch (err) {
    syncs.delete(id);
    sync.destroy();
    toast.error(errorMessage(err));
    return null;
  } finally {
    creating.delete(id);
  }
}

/** Live Server do projeto (ou abre o que já está no ar). */
export async function startLive(project: RunProject) {
  const current = activeRun(useLocalRuns.getState().runs, project.id, "live");
  if (current) {
    desktop?.runs.open(current.id);
    return current;
  }
  const run = await startRun(project, "live", (input) => desktop!.runs.startLive(input));
  if (run) toast.success("Pré-visualização no ar", { description: run.url ?? undefined });
  return run;
}

export async function startContainer(project: RunProject, spec: ContainerSpec) {
  const run = await startRun(project, "container", (input) =>
    desktop!.runs.startContainer({ ...input, spec }),
  );
  if (run)
    toast.success("Subindo o contêiner", { description: run.url ?? "Acompanhe a saída em Execuções." });
  return run;
}

export async function stopRun(id: string) {
  await desktop?.runs.stop(id);
}

const PLAN_FILE =
  /^(package\.json|pnpm-lock\.yaml|yarn\.lock|requirements\.txt|pyproject\.toml|manage\.py|go\.mod|composer\.json|pom\.xml|index\.php|(docker-)?compose\.ya?ml|(.+\/)?Dockerfile)$/;
const CODE_FILE = /^(src\/)?[^/]+\.(m?[jt]s|cjs)$/;

/**
 * Arquivos para detectar como rodar o projeto: todos os caminhos (conteúdo vazio) e o conteúdo
 * dos que importam (package.json, Dockerfile, Compose, requirements.txt…).
 */
export async function readPlanFiles(session: ProjectSession): Promise<Record<string, string>> {
  const tree = session.tree();
  const files: Record<string, string> = {};
  const read: Array<[string, string]> = [];
  let code = 0;
  for (const [id, path] of tree.paths) {
    if (tree.nodes.get(id)?.type !== "file" || isBlobNode(tree.nodes.get(id)) || SKIP.test(path)) continue;
    files[path] = "";
    if (PLAN_FILE.test(path) || (CODE_FILE.test(path) && code++ < 20)) read.push([id, path]);
  }
  await Promise.all(
    read.map(async ([id, path]) => {
      const handle = session.openFile(id);
      try {
        await session.loaded(handle, 8_000);
        files[path] = handle.text.toString();
      } finally {
        handle.release();
      }
    }),
  );
  return files;
}
